<template lang="pug">
.bulk-send-details.d-flex.flex-column.gap-3(v-if='bulkSend')
  .d-flex.flex-column.gap-2
    .d-flex.align-items-start.justify-content-between.flex-wrap.gap-3
      .d-flex.align-items-center.flex-wrap.gap-3
        h1.title.fs-4.m-0 {{ bulkSend.name }}
        span.status.d-inline-flex.align-items-center.rounded-pill.px-3.py-1.small
          icon.me-1(
            :class='getStatusIcon(displayStatus).class'
            :name='getStatusIcon(displayStatus).name'
          )
          | {{ getStatusText(displayStatus) }}
      .d-flex.align-items-center.gap-2
        button.renotify-button.btn.d-flex.align-items-center.gap-2(
          @click='openRenotifyPendingModal'
          type='button'
          v-if='canRenotifyPending'
        )
          icon(name='arrow-rotate-right')
          | {{ $t('bulkSendDetails.renotifyPending.button', { total: pendingCount }) }}
        button.cancel-button.btn.d-flex.align-items-center.gap-2(
          @click='openCancelModal'
          type='button'
          v-if='canCancel'
        )
          icon(name='ban')
          | {{ $t('bulkSendDetails.cancelBulkSend.button') }}
    .d-flex.flex-wrap.gap-3.small.text-muted
      span
        | {{ $t('bulkSendDetails.createdAt') }}
        |
        strong {{ bulkSend.created_at | dateTime }}
      span(v-if='bulkSend.sent_start')
        | {{ $t('bulkSendDetails.sentStart') }}
        |
        strong {{ bulkSend.sent_start | dateTime }}
      span(v-if='bulkSend.completed_at')
        | {{ $t('bulkSendDetails.completedAt') }}
        |
        strong {{ bulkSend.completed_at | dateTime }}
      span
        | {{ $t('bulkSendDetails.origin.label') }}:
        |
        strong {{ fileResult ? $t('bulkSendDetails.origin.csv') : $t('bulkSendDetails.origin.manual') }}
      span(v-if='fileResult')
        | {{ $t('bulkSendDetails.spreadsheet') }}:
        |
        strong {{ fileResult.name }}
      span(v-if='entriesTotal')
        | {{ $t('bulkSendDetails.recipientsCount') }}:
        |
        strong {{ entriesTotal }}

  .row.row-cols-2.row-cols-md-4.g-3
    .col(:key='card.key' v-for='card in summaryCards')
      .summary-card.h-100.d-flex.flex-column.gap-1.p-3.bg-white.border.rounded(
        :class='[card.key, { highlight: card.highlight }]'
      )
        small(
          :class='{ "fw-bold": card.highlight, "text-muted": !card.highlight }'
        ) {{ card.label }}
        span.fs-3.fw-bold.lh-1 {{ card.value }}
        small.text-muted(v-if='card.hint') {{ card.hint }}

  section.d-flex.flex-column.gap-3.p-4.bg-white.border.rounded
    .d-flex.justify-content-between.align-items-baseline
      h2.fs-6.fw-bold.text-body.m-0 {{ $t('bulkSendDetails.envelopesSituation') }}
      small.text-muted
        | {{ $t('bulkSendDetails.updatedAt') }} {{ bulkSend.updated_at | dateTime }}
    .bar
      div(
        :class='item.key'
        :key='item.key'
        :style='{ width: item.percent }'
        :title='`${item.label}: ${item.value}`'
        v-for='item in envelopeStatusListWithValue'
      )
    .row.row-cols-2.row-cols-md-4.g-3
      .col(:key='item.key' v-for='item in envelopeStatusList')
        small.d-flex.align-items-center.gap-2
          span.dot(:class='item.key')
          | {{ item.label }}
        .d-flex.align-items-center.gap-2.ps-4
          span.fs-5.fw-bold {{ item.value }}
          span.badge.rounded-pill.bg-light.text-muted.fw-normal {{ item.percent }}

  bulk-send-failed-entries(
    :bulk-send-uuid='bulkSend.uuid'
    :can-retry='canRetry'
    @retried='refresh'
    v-if='hasFailedEntries'
  )

  section.bg-white.border.rounded.mb-5
    .d-flex.justify-content-start.align-items-center.flex-wrap.p-3.border-bottom
      .form-control-icon--end-line.search.w-100
        input.form-control.rounded-3(
          :aria-label='$t("bulkSendDetails.searchPlaceholder")'
          :placeholder='$t("bulkSendDetails.searchPlaceholder")'
          autocomplete='off'
          v-model.trim='filters.search'
        )
        icon.icon--blue.font-size-subtitle(name='magnifying-glass')
      filter-selector.me-auto(
        :filters='entryFilterConfig'
        @update='updateEntryFilter'
      )

      button.status-help-button.btn.d-flex.align-items-center.justify-content-center(
        :aria-label='$t("bulkSendDetails.statusHelp.button")'
        :title='$t("bulkSendDetails.statusHelp.button")'
        @click='openStatusHelpModal'
        type='button'
      )
        icon(name='circle-question')
    template(v-if='entries.data.length')
      bulk-send-entries-card-table(
        :data='entries.data'
        :first-line='entries.meta.from || 1'
        :search='filters.search'
        :status='filters.status'
      )
      .p-3
        simple-paginator(:payload='entries' @navigate='fetchEntries')
    .text-center.py-5(v-else)
      img.img-fluid(src='/img/ilustrations/no-contacts-found.svg')
      p.font-color-blue.text-center.mt-4 {{ $t('message.no_results_found') }}

  renotify-pending-bulk-send-modal(
    :loading='renotifyLoading'
    :pending='pendingCount'
    @confirm='renotifyPending'
    ref='renotifyPendingModal'
  )

  cancel-bulk-send-modal(
    :completed='statistics[BulkSendStatistic.COMPLETED]'
    :loading='cancelLoading'
    :pending='pendingCount'
    @confirm='cancelBulkSend'
    ref='cancelModal'
  )

  bulk-send-status-help-modal(ref='statusHelpModal')
</template>

<script>
import Icon from '@/ui/atoms/Icon.vue';
import SimplePaginator from '@/ui/organisms/SimplePaginator.vue';
import FilterSelector from '@/ui/organisms/FilterSelector.vue';
import BulkSendEntriesCardTable from '@/modules/business/components/organisms/card-table/BulkSendEntriesCardTable.vue';
import RenotifyPendingBulkSendModal from '@/modules/business/components/organisms/modals/RenotifyPendingBulkSendModal.vue';
import CancelBulkSendModal from '@/modules/business/components/organisms/modals/CancelBulkSendModal.vue';
import BulkSendStatusHelpModal from '@/modules/business/components/organisms/modals/BulkSendStatusHelpModal.vue';
import BulkSendFailedEntries from '@/modules/business/components/organisms/BulkSendFailedEntries.vue';
import EnvelopesService from '@/modules/business/services/http/envelopes/EnvelopesService';
import BulkSendService from '@/modules/business/services/BulkSendService';
import BulkSendStatus from '@/enums/BulkSendStatus';
import BulkSendEntryStatus from '@/enums/BulkSendEntryStatus';
import BulkSendStatistic from '@/enums/BulkSendStatistic';
import alert from '@/modules/account/common/alert';

export default {
  name: 'DocumentsBulkSendDetailsPage',
  components: {
    Icon,
    SimplePaginator,
    FilterSelector,
    BulkSendEntriesCardTable,
    RenotifyPendingBulkSendModal,
    CancelBulkSendModal,
    BulkSendStatusHelpModal,
    BulkSendFailedEntries,
  },
  data() {
    return {
      bulkSend: null,
      entries: {
        data: [],
        links: {},
        meta: {},
      },
      renotifyLoading: false,
      cancelLoading: false,
      filters: {
        status: '',
        search: '',
      },
      BulkSendStatistic,
    };
  },
  created() {
    this.$store.dispatch('page/setTitle', this.$t('pages.bulkSend.tabTitle'));
    this.$store.dispatch('page/setHead', {
      title: this.$t('bulkSend'),
      action: { name: 'business.documents.bulk-send' },
    });
    this.$store.dispatch('page/setClass', 'business documents');
    this.refresh();
  },
  computed: {
    displayStatus() {
      return BulkSendService.getDisplayStatus({
        status: this.bulkSend.status,
        envelopes: {
          total:
            this.statistics[BulkSendStatistic.IN_TRANSIT] +
            this.statistics[BulkSendStatistic.COMPLETED] +
            this.statistics[BulkSendStatistic.FAILED],
          completed: this.statistics[BulkSendStatistic.COMPLETED],
        },
      });
    },
    statistics() {
      const apiStatistics = this.bulkSend.statistics || {};
      const statistics = {};
      BulkSendStatistic.values.forEach((key) => {
        statistics[key] = apiStatistics[key] || 0;
      });
      return statistics;
    },
    fileResult() {
      return this.bulkSend.file_result;
    },
    generatedCount() {
      return (
        this.statistics[BulkSendStatistic.TOTAL] -
        this.statistics[BulkSendStatistic.PROCESSING]
      );
    },
    recipientsHint() {
      if (!this.fileResult) {
        return '';
      }

      if (this.fileResult.invalid_rows) {
        return this.$t('bulkSendDetails.summaryCards.invalidRows', {
          total: this.fileResult.invalid_rows,
        });
      }

      return this.$t('bulkSendDetails.summaryCards.fromSpreadsheet', {
        name: this.fileResult.name,
      });
    },
    summaryCards() {
      const processing = this.statistics[BulkSendStatistic.PROCESSING];
      const completed = this.statistics[BulkSendStatistic.COMPLETED];

      return [
        {
          key: 'recipients',
          label: this.$t('bulkSendDetails.summaryCards.recipients'),
          value: this.statistics[BulkSendStatistic.TOTAL],
          hint: this.recipientsHint,
        },
        {
          key: 'generated',
          label: this.$t('bulkSendDetails.summaryCards.generated'),
          value: this.generatedCount,
          hint: processing
            ? this.$t('bulkSendDetails.summaryCards.queued', {
                total: processing,
              })
            : '',
        },
        {
          key: 'signed',
          label: this.$t('bulkSendDetails.summaryCards.signed'),
          value: completed,
          hint: this.generatedCount
            ? this.$t('bulkSendDetails.summaryCards.signedPercent', {
                percent: this.percent(completed, this.generatedCount),
              })
            : '',
          highlight: true,
        },
        {
          key: 'awaiting',
          label: this.$t('bulkSendDetails.summaryCards.awaiting'),
          value: this.pendingCount,
          hint: this.pendingCount
            ? this.$t(
                this.canRenotifyPending
                  ? 'bulkSendDetails.summaryCards.canRenotify'
                  : 'bulkSendDetails.summaryCards.alreadySent'
              )
            : '',
        },
      ];
    },
    envelopeStatusList() {
      return BulkSendStatistic.chartOrder.map((key) => ({
        key,
        label: this.getStatisticText(key),
        value: this.statistics[key],
        percent: this.percent(
          this.statistics[key],
          this.statistics[BulkSendStatistic.TOTAL]
        ),
      }));
    },
    envelopeStatusListWithValue() {
      return this.envelopeStatusList.filter((item) => item.value > 0);
    },
    entriesTotal() {
      return this.entries.meta.total || 0;
    },
    entryFilterConfig() {
      return [
        {
          name: 'status',
          label: this.$t('bulkSendDetails.table.status'),
          selected: this.filters.status,
          options: [
            {
              label: this.$t('sentEnvelopesCardTable.filter.all'),
              value: '',
            },
          ].concat(BulkSendEntryStatus.getAllStatusTranslated(this.$i18n)),
        },
      ];
    },
    hasFailedEntries() {
      return this.statistics[BulkSendStatistic.FAILED] > 0;
    },
    pendingCount() {
      return this.statistics[BulkSendStatistic.IN_TRANSIT];
    },
    canCancel() {
      return [BulkSendStatus.COMPLETED, BulkSendStatus.FAILED].includes(
        this.bulkSend.status
      );
    },
    canRetry() {
      return [BulkSendStatus.COMPLETED, BulkSendStatus.FAILED].includes(
        this.bulkSend.status
      );
    },
    canRenotifyPending() {
      return this.pendingCount > 0;
    },
  },
  methods: {
    async refresh() {
      await Promise.all([this.fetchBulkSend(), this.fetchEntries()]);
    },
    async fetchEntries(page = 1) {
      try {
        const { data } = await EnvelopesService.listBulkSendEntries(
          this.$route.params.uuid,
          { page }
        );

        this.entries = {
          ...data,
          data: await this.withEnvelopeStates(data.data),
        };
      } catch {
        alert.fireAlert(this.$t('bulkSendDetails.detailsError'), {
          classes: 'alert-danger',
          styles:
            'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
          tag: 'httpAlert',
          icon: 'triangle-exclamation',
        });
      }
    },
    withEnvelopeStates(entries = []) {
      return Promise.all(
        entries.map(async (entry) => {
          if (!entry.envelope) {
            return entry;
          }

          const envelopeState = await EnvelopesService.getItem(entry.envelope)
            .then((response) => response.data.state)
            .catch(() => null);

          return { ...entry, envelope_state: envelopeState };
        })
      );
    },
    async fetchBulkSend() {
      this.$loading(true);

      try {
        const { data } = await EnvelopesService.getBulkSend(
          this.$route.params.uuid
        );

        this.bulkSend = data;
      } catch {
        alert.fireAlert(this.$t('bulkSendDetails.detailsError'), {
          classes: 'alert-danger',
          styles:
            'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
          tag: 'httpAlert',
          icon: 'triangle-exclamation',
        });
        this.$router.push({ name: 'business.documents.bulk-send' });
      } finally {
        this.$loading(false);
      }
    },
    updateEntryFilter(selectedFilters) {
      Object.keys(selectedFilters).forEach((key) => {
        this.filters[key] = selectedFilters[key].value;
      });
    },
    getStatusIcon(status) {
      return BulkSendStatus.getIcon(status);
    },
    getStatusText(status) {
      return BulkSendStatus.getText(status, this.$i18n);
    },
    getStatisticText(key) {
      return BulkSendStatistic.getText(key, this.$i18n);
    },
    percent(value, total) {
      return total ? `${Math.round((value / total) * 100)}%` : '0%';
    },
    openRenotifyPendingModal() {
      this.$refs.renotifyPendingModal.open();
    },
    async renotifyPending() {
      this.renotifyLoading = true;

      try {
        await EnvelopesService.reNotifyBulkSend(this.bulkSend.uuid);
        this.$refs.renotifyPendingModal.close();
        alert.fireAlert(
          this.$t('bulkSendDetails.renotifyPending.success', {
            total: this.pendingCount,
          }),
          {
            classes: 'alert-success',
            styles:
              'background-color: #d4edda; border-color: #c3e6cb; color: #155724;',
            icon: 'circle-check',
          }
        );
      } catch {
        alert.fireAlert(this.$t('bulkSendDetails.renotifyPending.error'), {
          classes: 'alert-danger',
          styles:
            'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
          tag: 'httpAlert',
          icon: 'triangle-exclamation',
        });
      } finally {
        this.renotifyLoading = false;
      }
    },
    openCancelModal() {
      this.$refs.cancelModal.open();
    },
    openStatusHelpModal() {
      this.$refs.statusHelpModal.open();
    },
    async cancelBulkSend() {
      this.cancelLoading = true;
      const cancelled = this.pendingCount;

      try {
        await EnvelopesService.cancelBulkSend(this.bulkSend.uuid);
        this.$refs.cancelModal.close();
        alert.fireAlert(
          this.$t('bulkSendDetails.cancelBulkSend.success', {
            total: cancelled,
          }),
          {
            classes: 'alert-success',
            styles:
              'background-color: #d4edda; border-color: #c3e6cb; color: #155724;',
            icon: 'circle-check',
          }
        );
        await this.refresh();
      } catch {
        alert.fireAlert(this.$t('bulkSendDetails.cancelBulkSend.error'), {
          classes: 'alert-danger',
          styles:
            'background-color: #f8d7da; border-color: #F5C6CB; color: #721C24;',
          tag: 'httpAlert',
          icon: 'triangle-exclamation',
        });
      } finally {
        this.cancelLoading = false;
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
.completed
  --status-color: #0E7A6F
.in-transit
  --status-color: #4AA8E8
.processing
  --status-color: #C9D2D2
.failed
  --status-color: #C0392B

.title
  color: #3D5A60
.status
  background-color: #E3F0FA
  color: #0A5A8A
  font-weight: 600

.summary-card
  &.highlight
    border: 2px solid #1AA592 !important
    color: #0E6B5F
    small
      color: inherit !important

.bar
  display: flex
  gap: 2px
  height: 16px
  border-radius: 8px
  overflow: hidden
  background-color: #E9EEEE
  div
    background-color: var(--status-color)

.dot
  flex-shrink: 0
  width: 10px
  height: 10px
  border-radius: 2px
  background-color: var(--status-color)

.search
  max-width: 280px

.status-help-button
  width: 40px
  height: 40px
  border: 0
  border-radius: 6px
  background-color: transparent
  color: #0A6EA8
  font-size: 20px
  &:hover
    background-color: #E3F0FA
    color: #07537F

.cancel-button
  border: 1.5px solid #B42318
  background-color: #FFFFFF
  color: #B42318
  font-weight: 600
  &:hover
    background-color: #FBEAEA
    color: #B42318

.renotify-button
  border: 0
  background-color: #1284C9
  color: #FFFFFF
  font-weight: 500
  &:hover
    background-color: #0F6FA9
    color: #FFFFFF
</style>
